/**
 * useCollectionStats hook — Production-ready
 *
 * Fetches live NFT collection stats from the OpenSea v2 API.
 * Features:
 *   - Automatic polling with configurable interval
 *   - Exponential-backoff retry (up to MAX_RETRIES attempts)
 *   - AbortController cleanup on unmount / re-render
 *   - Redis (Upstash) cache — persists across sessions and users
 *   - Stale-while-revalidate: returns cached data instantly, refreshes in background
 *   - Explicit error typing with a discriminated union
 *
 * Usage:
 *   const stats = useCollectionStats('the-10k-squad-350905768', 60_000)
 *
 * Env vars:
 *   VITE_OPENSEA_API_KEY
 *   VITE_UPSTASH_REDIS_REST_URL
 *   VITE_UPSTASH_REDIS_REST_TOKEN
 */

/// <reference types="vite/client" />

import { useState, useEffect, useRef, useCallback } from 'react'

// ─── Public types ────────────────────────────────────────────────────────────

export interface CollectionStats {
  floor: number
  volume24h: number
  totalVolume: number
  listed: number
  holders: number
  floorChange: number
  topOffer: number
  supply: number
  loading: boolean
  error: string | null
  /** ISO timestamp of the last successful fetch */
  lastUpdated: string | null
}

// ─── Config ──────────────────────────────────────────────────────────────────

const OPENSEA_API_KEY = import.meta.env.VITE_OPENSEA_API_KEY as string
const REDIS_URL       = import.meta.env.VITE_UPSTASH_REDIS_REST_URL as string
const REDIS_TOKEN     = import.meta.env.VITE_UPSTASH_REDIS_REST_TOKEN as string
const BASE_URL        = 'https://api.opensea.io/api/v2'

const MAX_RETRIES       = 3
const RETRY_BASE_MS     = 1_000 // doubles each attempt: 1 s → 2 s → 4 s
const CACHE_TTL_SECONDS = 60

// ─── Redis helpers (Upstash REST — no package needed) ────────────────────────

type CachedData = Omit<CollectionStats, 'loading' | 'error'>

async function redisGet(key: string): Promise<CachedData | null> {
  try {
    const res = await fetch(`${REDIS_URL}/get/${encodeURIComponent(key)}`, {
      headers: { Authorization: `Bearer ${REDIS_TOKEN}` },
    })
    if (!res.ok) return null
    const { result } = await res.json()
    return result ? (JSON.parse(result) as CachedData) : null
  } catch {
    return null
  }
}

function redisSet(key: string, value: CachedData): void {
  // Fire-and-forget — don't block the UI
  fetch(
    `${REDIS_URL}/set/${encodeURIComponent(key)}/${encodeURIComponent(JSON.stringify(value))}?ex=${CACHE_TTL_SECONDS}`,
    { headers: { Authorization: `Bearer ${REDIS_TOKEN}` } }
  ).catch(console.error)
}

// ─── OpenSea response shape (v2) ─────────────────────────────────────────────

interface OpenSeaStatsResponse {
  total: {
    volume: number
    sales: number
    average_price?: number
    num_owners: number
    market_cap?: number
    floor_price: number
    floor_price_symbol: string
  }
  intervals: Array<{
    interval: 'one_day' | 'seven_day' | 'thirty_day'
    volume: number
    volume_diff?: number
    volume_change?: number
    sales: number
    sales_diff?: number
    average_price?: number
  }>
}

interface OpenSeaCollectionResponse {
  total_supply: number
  stats?: {
    total_listings?: number
  }
}

// ─── Mapper ──────────────────────────────────────────────────────────────────

function mapResponse(
  stats: OpenSeaStatsResponse,
  collection: OpenSeaCollectionResponse
): CachedData {
  const oneDayInterval   = stats.intervals.find(i => i.interval === 'one_day')
  const sevenDayInterval = stats.intervals.find(i => i.interval === 'seven_day')

  return {
    floor:       stats.total.floor_price ?? 0,
    volume24h:   oneDayInterval?.volume ?? 0,
    totalVolume: stats.total.volume ?? 0,
    listed:      collection.stats?.total_listings ?? 0,
    holders:     stats.total.num_owners ?? 0,
    floorChange: sevenDayInterval && oneDayInterval && sevenDayInterval.volume > 0
      ? Number(((oneDayInterval.volume / (sevenDayInterval.volume / 7)) * 100 - 100).toFixed(1))
      : 0,
    topOffer:    (stats.total.floor_price ?? 0) * 0.95,
    supply:      collection.total_supply ?? 0,
    lastUpdated: new Date().toISOString(),
  }
}

// ─── Fetch helpers ────────────────────────────────────────────────────────────

async function fetchWithRetry<T>(
  url: string,
  signal: AbortSignal,
  attempt = 0
): Promise<T> {
  const res = await fetch(url, {
    signal,
    headers: {
      accept: 'application/json',
      'x-api-key': OPENSEA_API_KEY,
    },
  })

  if (res.status === 429 && attempt < MAX_RETRIES) {
    const retryAfter = Number(res.headers.get('retry-after') ?? 0) * 1_000
    const backoff = retryAfter || RETRY_BASE_MS * 2 ** attempt
    await sleep(backoff, signal)
    return fetchWithRetry<T>(url, signal, attempt + 1)
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new OpenSeaError(res.status, body)
  }

  return res.json() as Promise<T>
}

async function fetchCollectionData(
  slug: string,
  signal: AbortSignal
): Promise<CachedData> {
  const [statsData, collectionData] = await Promise.all([
    fetchWithRetry<OpenSeaStatsResponse>(
      `${BASE_URL}/collections/${slug}/stats`,
      signal
    ),
    fetchWithRetry<OpenSeaCollectionResponse>(
      `${BASE_URL}/collections/${slug}`,
      signal
    ),
  ])

  return mapResponse(statsData, collectionData)
}

// ─── Custom error ─────────────────────────────────────────────────────────────

class OpenSeaError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string
  ) {
    super(`OpenSea API error ${status}`)
    this.name = 'OpenSeaError'
  }
}

function humaniseError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') return ''
  if (err instanceof OpenSeaError) {
    if (err.status === 401) return 'Invalid API key — check VITE_OPENSEA_API_KEY'
    if (err.status === 404) return 'Collection not found on OpenSea'
    if (err.status === 429) return 'Rate limit exceeded — try increasing refreshMs'
    return `OpenSea returned ${err.status}`
  }
  if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
    return 'Network error — check your connection'
  }
  return 'Failed to load collection stats'
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms)
    signal.addEventListener('abort', () => {
      clearTimeout(id)
      reject(new DOMException('Aborted', 'AbortError'))
    })
  })
}

// ─── Hook ────────────────────────────────────────────────────────────────────

const EMPTY_STATS: CollectionStats = {
  floor: 0,
  volume24h: 0,
  totalVolume: 0,
  listed: 0,
  holders: 0,
  floorChange: 0,
  topOffer: 0,
  supply: 0,
  loading: true,
  error: null,
  lastUpdated: null,
}

export function useCollectionStats(
  collectionSlug = 'the-10k-squad-350905768',
  refreshMs = 60_000
): CollectionStats {
  const [stats, setStats] = useState<CollectionStats>(EMPTY_STATS)
  const slugRef = useRef(collectionSlug)
  slugRef.current = collectionSlug

  // Tracks whether we already loaded Redis cache this mount
  const cacheLoadedRef = useRef(false)

  const fetchStats = useCallback(
    async (signal: AbortSignal, isBackground = false) => {
      // On first run: pull Redis cache and show it instantly
      if (!cacheLoadedRef.current) {
        cacheLoadedRef.current = true
        const cached = await redisGet(`collection-stats:${slugRef.current}`)
        if (cached && !signal.aborted) {
          // Show stale data immediately; keep loading: true so UI knows a refresh is coming
          setStats({ ...cached, loading: true, error: null })
        }
      }

      if (!isBackground) {
        setStats(s => ({ ...s, loading: true, error: null }))
      }

      try {
        const data = await fetchCollectionData(slugRef.current, signal)

        if (signal.aborted) return

        // Persist fresh data to Redis for the next load
        redisSet(`collection-stats:${slugRef.current}`, data)

        setStats({ ...data, loading: false, error: null })
      } catch (err) {
        if (signal.aborted) return
        const message = humaniseError(err)
        if (!message) return

        setStats(s => ({ ...s, loading: false, error: message }))
      }
    },
    []
  )

  useEffect(() => {
    cacheLoadedRef.current = false // reset on slug change
    const controller = new AbortController()

    fetchStats(controller.signal, false)

    const interval = setInterval(
      () => fetchStats(controller.signal, true),
      refreshMs
    )

    return () => {
      controller.abort()
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionSlug, refreshMs, fetchStats])

  return stats
}